import { Injectable } from '@angular/core';
import { NavItem } from 'src/app/layouts/full/vertical/sidebar/nav-item/nav-item';
import { PermissionsService } from './permissions/permissions.service';
import { PermissionMappingService } from './permissions/permission-mapping.service';
import { AuthenticationService } from './apps/Authentication/authentication.service';
import { ParkingProfileService } from './apps/tenant-profile/parking-profile.service';

/**
 * Visibilidad de ítems del menú lateral (vertical y horizontal), alineada con `AppNavItemComponent`.
 */
@Injectable({
  providedIn: 'root',
})
export class NavItemVisibilityService {
  constructor(
    private readonly permissionsService: PermissionsService,
    private readonly permissionMappingService: PermissionMappingService,
    private readonly authService: AuthenticationService,
    private readonly parkingProfileService: ParkingProfileService
  ) {}

  isVisible(item: NavItem): boolean {
    if (item.navCap && !item.displayName) {
      if (item.navCapSuperAdminOnly) {
        return this.isSuperAdmin();
      }
      if (this.isSuperAdmin()) {
        return false;
      }
      return this.isParkingProfileRequirementMet(item);
    }

    if (this.isSuperAdmin()) {
      return !!item.superAdminOnly;
    }

    if (item.tenantAdminOnly) {
      return this.authService.isTenantAdministrator(
        this.authService.getCurrentUserSnapshot()
      );
    }

    if (item.cashBoxSupervisionNav) {
      return this.isCashBoxSupervisionNavVisible();
    }

    if (!this.isParkingProfileRequirementMet(item)) {
      return false;
    }

    if (item.permission) {
      const permissions = Array.isArray(item.permission) ? item.permission : [item.permission];
      const mode = item.permissionMode || 'any';
      return mode === 'all'
        ? this.permissionsService.hasAllPermissions(permissions)
        : this.permissionsService.hasAnyPermission(permissions);
    }

    if (item.children && item.children.length) {
      return item.children.some((child) => this.isVisible(child));
    }

    if (item.route) {
      const requiredPermissions = this.permissionMappingService.getPermissionsForRoute(item.route);
      if (requiredPermissions.length > 0) {
        return this.permissionsService.hasAnyPermission(requiredPermissions);
      }
    }

    return true;
  }

  private isSuperAdmin(): boolean {
    return this.authService.isSuperAdministrator(this.authService.getCurrentUserSnapshot());
  }

  private isCashBoxSupervisionNavVisible(): boolean {
    const u = this.authService.getCurrentUserSnapshot();
    return (
      this.authService.isTenantAdministrator(u) ||
      this.authService.hasParkingSupervisorRole(u) ||
      this.permissionsService.hasPermission('CashBox.ViewAll')
    );
  }

  private isParkingProfileRequirementMet(item: NavItem): boolean {
    if (!item.requiresParkingProfile) {
      return true;
    }
    if (item.requiresParkingProfile === 'Acceso') {
      return this.parkingProfileService.hasAcceso();
    }
    return true;
  }
}
